import * as React from "react";
import { useParams } from "react-router-dom";
import OEContext from "../../layouts/OEContext";
import { Card, CardTitle, CardBody } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { axiosDownload } from "../../data/helpers/baseApi";
import { SERVICE_URLS } from "../../data/helpers/serviceUrls";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
  useQueryGetClientMenuDocuments,
  useQueryGetAllClientMenuAll,
  useQueryGetClientCurrentBenefitPlans,
} from "../../data/services/clientAdminService";
import { MultiSelect, DropDownList } from "@progress/kendo-react-dropdowns";
import { Loader } from "@progress/kendo-react-indicators";
import { Hint } from "@progress/kendo-react-labels";
import AlertDialog from "../../components/AlertDialog";
import {
  formatPlanDisplayName,
  getOfferType,
  getPlansForDocument,
  hasMultipleBenefitGroups,
  scopePlansByPrimary,
  withClientIdFileName,
} from "../shared/menuCompareUtils";
import TerminatedBadge, {
  TERMINATED_COLOR,
} from "../../components/TerminatedBadge";
import AetnaMultiCarrierCompare from "../shared/AetnaMultiCarrierCompare";

export const getVisibleCompareDocuments = (documents, previousYear) => {
  return documents || [];
};

const getPreviousYearCompareFileName = (documentType, docType) => {
  const normalizedDocumentType = documentType || "Medical";
  const extension = docType === "pdf" ? "pdf" : "xlsx";

  return `PrestigePEO Year-to-Year ${normalizedDocumentType} Comparison.${extension}`;
};

function sortDocumentsByType(docs) {
  const sortOrder = ["Medical", "Dental", "Vision"];
  return docs.sort((a, b) => {
    return sortOrder.indexOf(a.documentType) - sortOrder.indexOf(b.documentType);
  });
}

const ClientRateMenuCompare = () => {
  const { clientId, oePeriodId } = useParams();
  const oePeriod = React.useContext(OEContext);
  const resolvedOePeriodId = oePeriodId || oePeriod?.oePeriodId;

  const [gridDataSrc, setGridDataSrc] = React.useState([]);
  const [previousGridDataSrc, setPreviousGridDataSrc] = React.useState([]);
  const [prevOEPeriod, setPrevOEPeriod] = React.useState([]);
  const { isLoading: menuDocumentsLoading, data: menuDocuments } =
    useQueryGetClientMenuDocuments(resolvedOePeriodId, clientId);

  const { isLoading: menusAllLoading, data: allMenus } =
    useQueryGetAllClientMenuAll(resolvedOePeriodId, clientId);
  const { isLoading: prevMenusAllLoading, data: prevMenus } =
    useQueryGetAllClientMenuAll(prevOEPeriod, clientId);
  const { data: clientCurrentBenefitPlans } =
    useQueryGetClientCurrentBenefitPlans(clientId);

  React.useEffect(() => {
    if (!menuDocumentsLoading && menuDocuments && menuDocuments.data) {
      menuDocuments.data = sortDocumentsByType(menuDocuments.data);
      const currentMenus = menuDocuments.data.filter((e) => !e.isPreviousYear);
      const previousMenus = menuDocuments.data.filter((e) => e.isPreviousYear);
      if (previousMenus.length > 0) {
        setPrevOEPeriod(previousMenus[0].oePeriodId);
      }
      setGridDataSrc([...currentMenus]);
      setPreviousGridDataSrc([...previousMenus]);
    }
  }, [menuDocumentsLoading, menuDocuments]);

  if (menuDocumentsLoading) {
    return "Loading ...";
  }

  return (
    <div>
      <div style={{ paddingBottom: 10 }}>
        <h2 style={{ margin: "10px" }}>Full Rate Benefit Plan Comparisons</h2>
      </div>
      <div
        style={{
          display: "flex",
        }}
      >
        <RenderDocs
          data={gridDataSrc}
          menusAllLoading={menusAllLoading}
          menus={allMenus?.data}
        />
      </div>
      <div style={{ paddingBottom: 10 }}>
        <h2 style={{ margin: "10px", marginTop: "40px" }}>
          Current vs Renewal Year Full Rate Benefit Menus
        </h2>
      </div>
      <div
        style={{
          display: "flex",
        }}
      >
        <RenderDocs
          data={previousGridDataSrc}
          previousYear={true}
          menusAllLoading={prevMenusAllLoading}
          menus={
            clientCurrentBenefitPlans?.data?.length
              ? clientCurrentBenefitPlans.data
              : (prevMenus?.data?.length ? prevMenus.data : allMenus?.data)
          }
          currentOePeriod={resolvedOePeriodId}
        />
      </div>
      <AetnaMultiCarrierCompare
        clientId={clientId}
        oePeriodId={resolvedOePeriodId}
      />
    </div>
  );
};

const RenderDocs = ({ data, previousYear, menusAllLoading, menus, currentOePeriod }) => {
  const { clientId } = useParams();

  const [isDownloading, setIsDownloading] = React.useState(false);
  const [downloadId, setDownloadId] = React.useState(null);
  const [docType, setDocType] = React.useState(null);
  const [selectedDoc, setSelectedDoc] = React.useState(null);
  const [ratemenuFilterVisible, setRatemenuFilterVisible] = React.useState(false);

  const [showAlertDialog, setShowAlertDialog] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [selectedMedicalPlanCompare, setSelectedMedicalPlanCompare] = React.useState([]);
  const [selectedDentalPlanCompare, setSelectedDentalPlanCompare] = React.useState([]);
  const [selectedVisionPlanCompare, setSelectedVisionPlanCompare] = React.useState([]);

  const [selectedMedicalPlans, setSelectedMedicalPlan] = React.useState([]);
  const [selectedDentalPlan, setSelectedDentalPlan] = React.useState([]);
  const [selectedVisionPlan, setSelectedVisionPlan] = React.useState([]);

  const [planwithTypeAll, setPlanwithTypeAll] = React.useState([]);
  const [planwithType, setPlanwithType] = React.useState([]);
  const primaryPlanOptions = React.useMemo(
    () => (planwithTypeAll ?? []).filter((plan) => !plan.isTermed),
    [planwithTypeAll]
  );
  const informationalTermedPlans = React.useMemo(
    () => (planwithTypeAll ?? []).filter((plan) => plan.isTermed),
    [planwithTypeAll]
  );
  const hasTermedPlanOptions = React.useMemo(
    () => (planwithTypeAll ?? []).some((plan) => plan.isTermed),
    [planwithTypeAll]
  );

  const [opened, setOpened] = React.useState(false);

  const getPrimaryPlan = (type) => {
    if (type === "Medical") {
      return selectedMedicalPlanCompare;
    }
    if (type === "Dental") {
      return selectedDentalPlanCompare;
    }
    if (type === "Vision") {
      return selectedVisionPlanCompare;
    }

    return null;
  };

  const setPrimaryPlan = (type, plan) => {
    if (type === "Medical") {
      setSelectedMedicalPlanCompare(plan);
    } else if (type === "Dental") {
      setSelectedDentalPlanCompare(plan);
    } else if (type === "Vision") {
      setSelectedVisionPlanCompare(plan);
    }
  };

  const getSelectedPlans = (type) => {
    if (type === "Medical") {
      return selectedMedicalPlans;
    }
    if (type === "Dental") {
      return selectedDentalPlan;
    }
    if (type === "Vision") {
      return selectedVisionPlan;
    }

    return [];
  };

  const clearSelectedPlans = (type) => {
    if (type === "Medical") {
      setSelectedMedicalPlan([]);
    } else if (type === "Dental") {
      setSelectedDentalPlan([]);
    } else if (type === "Vision") {
      setSelectedVisionPlan([]);
    }
  };

  const clearSelections = () => {
    setSelectedMedicalPlanCompare([]);
    setSelectedDentalPlanCompare([]);
    setSelectedVisionPlanCompare([]);
    setSelectedMedicalPlan([]);
    setSelectedDentalPlan([]);
    setSelectedVisionPlan([]);
  };

  const resetSelections = () => {
    clearSelections();
    setSelectedDoc(null);
    setRatemenuFilterVisible(false);
    setShowAlertDialog(false);
  };

  const openDialog = (doc, type) => {
    clearSelections();
    setSelectedDoc(doc);
    setDocType(type);
    setShowAlertDialog(false);
    setRatemenuFilterVisible(true);
  };

  React.useEffect(() => {
    const currentDocType = selectedDoc?.documentType;
    const primaryPlan =
      currentDocType === "Medical"
        ? selectedMedicalPlanCompare
        : currentDocType === "Dental"
          ? selectedDentalPlanCompare
          : currentDocType === "Vision"
            ? selectedVisionPlanCompare
            : null;
    const plansForDocument = getPlansForDocument(menus, currentDocType);
    const scopedPlans = scopePlansByPrimary(plansForDocument, primaryPlan);

    const displayPlans = scopedPlans.map(formatPlanDisplayName);
    setPlanwithTypeAll(displayPlans);
    setPlanwithType(
      displayPlans.filter((plan) => !plan.isTermed && plan.planId !== primaryPlan?.planId)
    );
  }, [
    menus,
    selectedDoc,
    selectedMedicalPlanCompare,
    selectedDentalPlanCompare,
    selectedVisionPlanCompare,
  ]);

  const selectedPrimaryPlan = getPrimaryPlan(selectedDoc?.documentType);
  const selectedPlans = getSelectedPlans(selectedDoc?.documentType);
  const plansForSelectedDocument = getPlansForDocument(
    menus,
    selectedDoc?.documentType
  );
  const requiresBenefitGroupAnchor =
    hasMultipleBenefitGroups(plansForSelectedDocument) &&
    !selectedPrimaryPlan?.planId;

  const showAlert = (message) => {
    setAlertMessage(message);
    setShowAlertDialog(true);
    setIsDownloading(false);
  };

  const onChangeDropdown = (event, currentDocType) => {
    clearSelectedPlans(currentDocType);
    setPrimaryPlan(currentDocType, event.target.value);
  };

  const onChange = (event, currentDocType) => {
    if (currentDocType === "Medical") {
      setSelectedMedicalPlan(event.target.value);
    } else if (currentDocType === "Dental") {
      setSelectedDentalPlan(event.target.value);
    } else if (currentDocType === "Vision") {
      setSelectedVisionPlan(event.target.value);
    }
  };

  const downloadFileLastYear = async (
    item,
    currentDocType,
    isAllMenus,
    currentOePeriodId
  ) => {
    setIsDownloading(true);
    setDocType(currentDocType);
    setDownloadId(isAllMenus ? item.clientMenuDocumentId : null);

    const fileName = withClientIdFileName(
      getPreviousYearCompareFileName(selectedDoc?.documentType, currentDocType),
      clientId
    );

    const selectedPlanIds = selectedPlans.map((plan) => plan.planId);
    const planIds = isAllMenus
      ? planwithTypeAll.map((plan) => plan.planId)
      : selectedPrimaryPlan?.planId
        ? [
            selectedPrimaryPlan.planId,
            ...selectedPlanIds.filter(
              (planId) => planId !== selectedPrimaryPlan.planId
            ),
          ]
        : selectedPlanIds;

    if (planIds.length === 0) {
      showAlert("Please select at least one plan to compare.");
      return;
    }

    const offerType = getOfferType(selectedDoc?.documentType);
    await axiosDownload(
      SERVICE_URLS.CLIENT_ADMIN_MENU_DOCUMENT_DOWNLOAD_PREVYEARCOMPAREv2(
        currentOePeriodId,
        item.oePeriodId,
        clientId,
        item.clientMenuDocumentId,
        currentDocType,
        planIds,
        offerType
      ),
      fileName
    );

    resetSelections();
    setIsDownloading(false);
  };

  const downloadFile = async (item, currentDocType, isAllMenus) => {
    setIsDownloading(true);
    setDocType(currentDocType);
    setDownloadId(isAllMenus ? item.clientMenuDocumentId : null);

    const fileName = withClientIdFileName(
      currentDocType === "pdf" ? item.fileName : item.excelFileName,
      clientId
    );

    if (!selectedPrimaryPlan?.planId) {
      showAlert("Please select a primary plan to compare other plans against.");
      return;
    }

    if (!isAllMenus && selectedPlans.length === 0) {
      showAlert("Please select plans to compare against the primary plan.");
      return;
    }

    const planIds = isAllMenus
      ? planwithTypeAll.map((plan) => plan.planId)
      : [selectedPrimaryPlan.planId, ...selectedPlans.map((plan) => plan.planId)];

    const offerType = getOfferType(selectedDoc?.documentType);
    await axiosDownload(
      SERVICE_URLS.CLIENT_ADMIN_MENU_COMPARE_DOWNLOAD(
        item.oePeriodId,
        clientId,
        item.clientMenuDocumentId,
        currentDocType,
        planIds,
        offerType
      ),
      fileName
    );

    resetSelections();
    setIsDownloading(false);
  };

  data = getVisibleCompareDocuments(data, previousYear);
  if (data.length === 0) {
    if (previousYear) {
      return <div>No benefit menu documents found for previous year.</div>;
    }
    return <div>No benefit menu documents found.</div>;
  }

  const primaryPlaceholder = previousYear
    ? "Select a plan to scope the comparison group..."
    : "Select primary plan to compare against...";
  const showDownloadAllButton = previousYear
    ? ((selectedPlans.length === 0 && !selectedPrimaryPlan?.planId) ||
        planwithType.length === 0)
    : selectedPlans.length === 0 || planwithType.length === 0;

  return data.map((doc, index) => {
    return (
      <Card style={{ width: 300, margin: "10px" }} key={index} type="info">
        {ratemenuFilterVisible && selectedDoc && (
          <Dialog
            title={`${selectedDoc.documentType} Benefit Filter Menus`}
            onClose={resetSelections}
            width={"60vw"}
          >
            <div>
              {!previousYear && hasTermedPlanOptions && (
                <>
                  <p>
                    Terminated plans are informational only and cannot be selected in compare
                    downloads. When a replacement exists, the displayed comparison premium
                    comes from the mapped replacement plan.
                  </p>
                  <div style={{ marginBottom: "10px" }}>
                    {informationalTermedPlans.map((plan) => (
                      <div key={`info-${plan.planId}`}>
                        <span style={{ color: TERMINATED_COLOR }}>
                          {plan.planName}
                        </span>
                        <TerminatedBadge plain />
                        {!plan.hasReplacementPlan && (
                          <span
                            style={{ color: TERMINATED_COLOR, marginLeft: 6 }}
                          >
                            No replacement mapped
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {planwithTypeAll?.length !== 0 ? (
                <>
                  {!previousYear && (
                    <DropDownList
                      data={primaryPlanOptions}
                      onChange={(event) =>
                        onChangeDropdown(event, selectedDoc.documentType)
                      }
                      textField="planName"
                      placeholder={primaryPlaceholder}
                      value={selectedPrimaryPlan}
                      dataItemKey="planId"
                      style={{ width: "100%" }}
                    />
                  )}

                  {!previousYear && requiresBenefitGroupAnchor && (
                    <Hint>
                      Choose a primary plan first to keep comparisons within one benefit group.
                    </Hint>
                  )}

                  <MultiSelect
                    data={previousYear ? planwithTypeAll : planwithType}
                    onChange={(event) => onChange(event, selectedDoc.documentType)}
                    onBlur={() => setOpened(false)}
                    onOpen={() => setOpened(true)}
                    opened={opened}
                    loading={menusAllLoading}
                    dataItemKey="planId"
                    max
                    textField="planName"
                    value={selectedPlans}
                    label="Select plans to compare."
                    placeholder="Please select ..."
                    disabled={!previousYear && requiresBenefitGroupAnchor}
                    style={{ width: "100%" }}
                  />
                </>
              ) : (
                <div>
                  There are no multiple plans to be select.
                  Please click download button to get plan details
                </div>
              )}
            </div>

            {showAlertDialog && (
              <AlertDialog
                title={"Alert!"}
                message={alertMessage}
                onClose={() => setShowAlertDialog(false)}
              />
            )}
            <DialogActionsBar layout={"end"}>
              <div style={{ paddingRight: "8px", paddingBottom: "8px" }}>
                {previousYear ? (
                  <Button
                    disabled={isDownloading || selectedPlans.length === 0}
                    themeColor={"primary"}
                    onClick={() =>
                      downloadFileLastYear(
                        selectedDoc,
                        docType,
                        false,
                        currentOePeriod
                      )
                    }
                    style={{ marginRight: "10px" }}
                  >
                    {isDownloading ? (
                      <Loader
                        style={{ marginBottom: 2, marginLeft: 5 }}
                        size="small"
                        type={"infinite-spinner"}
                        themeColor="light"
                      />
                    ) : null}{" "}
                    {"Download"}
                  </Button>
                ) : showDownloadAllButton ? (
                  <Button
                    disabled={isDownloading}
                    themeColor={"primary"}
                    onClick={() => downloadFile(selectedDoc, docType, true)}
                    style={{ marginRight: "10px" }}
                  >
                    {isDownloading ? (
                      <Loader
                        style={{ marginBottom: 2, marginLeft: 5 }}
                        size="small"
                        type={"infinite-spinner"}
                        themeColor="light"
                      />
                    ) : null}{" "}
                    {"Download All"}
                  </Button>
                ) : (
                  <Button
                    disabled={isDownloading}
                    themeColor={"primary"}
                    onClick={() => downloadFile(selectedDoc, docType, false)}
                    style={{ marginRight: "10px" }}
                  >
                    {isDownloading ? (
                      <Loader
                        style={{ marginBottom: 2, marginLeft: 5 }}
                        size="small"
                        type={"infinite-spinner"}
                        themeColor="light"
                      />
                    ) : null}{" "}
                    {"Download Selected"}
                  </Button>
                )}
                <Button onClick={resetSelections} style={{ marginRight: "10px" }}>
                  {"Close"}
                </Button>
              </div>
            </DialogActionsBar>
          </Dialog>
        )}

        <CardBody>
          <CardTitle>
            <b>{doc.documentType} Benefit Menu</b>
          </CardTitle>
          <p>Please select the desired file format below to initiate the download</p>
          <p>
            {!previousYear && doc.fileName && (
              <Button
                themeColor={"primary"}
                className={"oe-multiline-button k-button-flat k-button-flat-primary"}
                onClick={() => openDialog(doc, "pdf")}
                disabled={isDownloading}
              >
                {doc.fileName}{" "}
                {isDownloading &&
                downloadId === doc.clientMenuDocumentId &&
                docType === "pdf" ? (
                  <span className="k-icon k-i-loading"></span>
                ) : (
                  ""
                )}
              </Button>
            )}
          </p>
          <p>
            {doc.excelFileName && (
              <Button
                themeColor={"primary"}
                className={"oe-multiline-button k-button-flat k-button-flat-primary"}
                onClick={() => openDialog(doc, "excel")}
                disabled={isDownloading}
              >
                {previousYear
                  ? getPreviousYearCompareFileName(doc.documentType, "excel")
                  : doc.excelFileName}{" "}
                {isDownloading &&
                downloadId === doc.clientMenuDocumentId &&
                docType === "excel" ? (
                  <span className="k-icon k-i-loading"></span>
                ) : (
                  ""
                )}
              </Button>
            )}
          </p>
        </CardBody>
      </Card>
    );
  });
};

export default ClientRateMenuCompare;
